/**
 * CustomerDisplayScreen - Müşteri Ekranı (İkinci Ekran)
 * 
 * POS cihazlarındaki müşteriye dönük ikinci ekran için:
 * - Reklam slider gösterimi (carousel tarzı)
 * - Sepet görüntüleme (BroadcastChannel ile senkronize)
 * - Tam ekran desteği
 * 
 * URL: /customer-display/:screenId?companyId={companyId}
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BRAND } from '../../../config/brand';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  keyframes,
} from '@mui/material';
import {
  getCustomerDisplaySettings,
  CustomerDisplaySettingsDto,
  CustomerDisplayImageDto,
} from '../../../services/salesService';

// ════════════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════════════

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface BroadcastMessage {
  type: 'CART_UPDATE' | 'SHOW_CART' | 'HIDE_CART' | 'SALE_COMPLETE';
  cart?: CartItem[];
  total?: number;
  screenId?: number;
}

// ════════════════════════════════════════════════════════════════════════════════
// KEYFRAMES - Animasyon tanımları
// ════════════════════════════════════════════════════════════════════════════════

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const zoomIn = keyframes`
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const flipIn = keyframes`
  from { transform: perspective(400px) rotateY(90deg); opacity: 0; }
  to { transform: perspective(400px) rotateY(0deg); opacity: 1; }
`;

// ════════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ════════════════════════════════════════════════════════════════════════════════

const CustomerDisplayScreen: React.FC = () => {
  const { screenId } = useParams<{ screenId: string }>();
  const [searchParams] = useSearchParams();
  
  // ✅ CompanyId: Önce localStorage'dan oku (güvenlik için URL'de gösterilmiyor)
  // Geriye uyumluluk için URL'den de kontrol et
  const companyId = localStorage.getItem('__customerDisplayCompanyId') || searchParams.get('companyId') || '';

  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<CustomerDisplaySettingsDto | null>(null);
  
  // Slider - Carousel için
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showCart, setShowCart] = useState(false);
  
  // Cart (BroadcastChannel'dan gelen)
  const [cart, setCart] = useState<CartItem[]>([]);
  const [total, setTotal] = useState(0);
  
  // Timers
  const slideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cartTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // BroadcastChannel
  const channelRef = useRef<BroadcastChannel | null>(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // LOAD SETTINGS
  // ─────────────────────────────────────────────────────────────────────────────
  
  const loadSettings = useCallback(async (retryCount = 0) => {
    if (!screenId || !companyId) {
      setError('Ekran ID veya Şirket ID eksik');
      setLoading(false);
      return;
    }
    
    try {
      console.log('📺 [CustomerDisplay] Ayarlar yükleniyor...', { screenId, companyId, retryCount });
      const response = await getCustomerDisplaySettings(parseInt(screenId));
      
      if (response.Success && response.Data) {
        setSettings(response.Data);
        console.log('✅ [CustomerDisplay] Ayarlar yüklendi:', response.Data);
        console.log('📺 [CustomerDisplay] Resim sayısı:', response.Data.Images?.length || 0);
        if (response.Data.Images) {
          response.Data.Images.forEach((img, i) => {
            console.log(`📺 [CustomerDisplay] Resim ${i + 1}: ${img.ImageUrl}`);
          });
        }
      } else {
        if (retryCount < 3) {
          console.warn(`📺 [CustomerDisplay] Ayarlar alınamadı, ${retryCount + 1}. deneme (2sn sonra)...`);
          setTimeout(() => loadSettings(retryCount + 1), 2000);
          return;
        }
        setError('Müşteri ekranı ayarları bulunamadı');
      }
    } catch (err: any) {
      console.error('❌ [CustomerDisplay] Hata:', err);
      if (retryCount < 3) {
        console.warn(`📺 [CustomerDisplay] Hata oluştu, ${retryCount + 1}. deneme (2sn sonra)...`);
        setTimeout(() => loadSettings(retryCount + 1), 2000);
        return;
      }
      setError('Ayarlar yüklenirken bir hata oluştu');
    } finally {
      if (retryCount >= 3 || !error) {
        setLoading(false);
      }
    }
  }, [screenId, companyId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // ─────────────────────────────────────────────────────────────────────────────
  // PENCERE BAŞLIĞI - Müşteri Ekranı için özel başlık
  // ─────────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    // Önceki başlığı sakla
    const originalTitle = document.title;
    
    // Müşteri ekranı için özel başlık
    document.title = 'MT Teknoloji ve Bilgisayar Sistemleri';
    
    // Component unmount olduğunda eski başlığı geri yükle
    return () => {
      document.title = originalTitle;
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // BROADCAST CHANNEL - Sepet Senkronizasyonu
  // ─────────────────────────────────────────────────────────────────────────────
  
  useEffect(() => {
    if (!screenId) return;
    
    const channelName = `customer-display-${screenId}`;
    console.log('📡 [CustomerDisplay] BroadcastChannel bağlanıyor:', channelName);
    
    channelRef.current = new BroadcastChannel(channelName);
    
    channelRef.current.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      console.log('📨 [CustomerDisplay] Mesaj alındı:', event.data);
      
      const { type, cart: newCart, total: newTotal, keepCartVisible } = event.data as any;
      
      switch (type) {
        case 'CART_UPDATE':
          if (newCart) setCart(newCart);
          if (newTotal !== undefined) setTotal(newTotal);
          
          // ✅ YENİ: Sepette ürün varsa süresiz sepet göster (timer yok)
          // Sepet boşaldığında reklama dön
          if (settings?.ShowCartOnActivity) {
            if (newCart && newCart.length > 0) {
              setShowCart(true);
              // ✅ Timer başlatma - sepet boşalana kadar sepette kal
              // resetCartTimer() kaldırıldı
            } else {
              // Sepet boşaldı, reklama dön
              setShowCart(false);
            }
          }
          break;
          
        case 'SHOW_CART':
          setShowCart(true);
          // resetCartTimer() kaldırıldı - süresiz sepet
          break;
          
        case 'HIDE_CART':
          // ✅ Sepet boşaldığında bu mesaj gelir, reklama dön
          setCart([]);
          setTotal(0);
          setShowCart(false);
          break;
          
        case 'SALE_COMPLETE':
          setCart([]);
          setTotal(0);
          setShowCart(false);
          break;
      }
    };
    
    return () => {
      console.log('📡 [CustomerDisplay] BroadcastChannel kapatılıyor');
      channelRef.current?.close();
    };
  }, [screenId, settings?.ShowCartOnActivity]);

  // ─────────────────────────────────────────────────────────────────────────────
  // SLIDE TIMER - Reklam geçişi (Carousel)
  // ─────────────────────────────────────────────────────────────────────────────
  
  const goToNextSlide = useCallback(() => {
    if (!settings || !settings.Images || settings.Images.length <= 1) return;
    
    setIsTransitioning(true);
    
    // Transition sonrası index'i güncelle
    setTimeout(() => {
      setCurrentImageIndex(prev => (prev + 1) % settings.Images.length);
      setIsTransitioning(false);
    }, settings.AnimationType === 'slide' ? 500 : 0);
  }, [settings]);

  const startSlideTimer = useCallback(() => {
    if (!settings || !settings.Images || settings.Images.length <= 1) return;
    
    slideTimerRef.current = setInterval(() => {
      goToNextSlide();
    }, settings.SlideInterval * 1000);
  }, [settings, goToNextSlide]);

  const stopSlideTimer = useCallback(() => {
    if (slideTimerRef.current) {
      clearInterval(slideTimerRef.current);
      slideTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!showCart && settings?.IsEnabled) {
      startSlideTimer();
    } else {
      stopSlideTimer();
    }
    
    return () => stopSlideTimer();
  }, [showCart, settings, startSlideTimer, stopSlideTimer]);

  // ─────────────────────────────────────────────────────────────────────────────
  // CART TIMER - Devre dışı (sepette ürün olduğu sürece sepet görünsün)
  // ─────────────────────────────────────────────────────────────────────────────
  // NOT: Cart timer artık kullanılmıyor. Sepette ürün olduğu sürece
  // müşteri ekranında sepet gösterilir. Sepet boşaldığında reklama döner.
  
  useEffect(() => {
    return () => {
      if (cartTimerRef.current) {
        clearTimeout(cartTimerRef.current);
      }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // FULLSCREEN REQUEST
  // ─────────────────────────────────────────────────────────────────────────────
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFullscreenHint, setShowFullscreenHint] = useState(true);
  
  const requestFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        console.log('🖥️ [CustomerDisplay] Zaten tam ekran modunda');
        setIsFullscreen(true);
        setShowFullscreenHint(false);
        return true;
      }
      
      if (document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen();
        console.log('🖥️ [CustomerDisplay] Tam ekran modu aktif');
        setIsFullscreen(true);
        setShowFullscreenHint(false);
        return true;
      }
    } catch (err) {
      console.warn('⚠️ [CustomerDisplay] Tam ekran isteği reddedildi:', err);
    }
    return false;
  }, []);

  // Fullscreen değişikliklerini dinle ve parent'a bildir
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFs = !!document.fullscreenElement;
      setIsFullscreen(isFs);
      setShowFullscreenHint(!isFs);
      console.log('🖥️ [CustomerDisplay] Fullscreen durumu:', isFs);
      
      // Parent pencereye durum bilgisi gönder
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage(
            { type: isFs ? 'CUSTOMER_DISPLAY_FULLSCREEN_SUCCESS' : 'CUSTOMER_DISPLAY_FULLSCREEN_EXIT' },
            '*'
          );
          console.log('📤 [CustomerDisplay] Parent\'a fullscreen durumu gönderildi:', isFs);
        } catch (err) {
          console.warn('⚠️ [CustomerDisplay] Parent\'a mesaj gönderilemedi:', err);
        }
      }
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Parent pencereden gelen mesajları dinle (REQUEST_FULLSCREEN, REQUEST_FULLSCREEN_DELEGATED)
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'REQUEST_FULLSCREEN' || event.data?.type === 'REQUEST_FULLSCREEN_DELEGATED') {
        console.log('📨 [CustomerDisplay] Tam ekran isteği alındı:', event.data?.type);
        
        // Capability Delegation ile geldiyse hemen dene
        const success = await requestFullscreen();
        
        if (success) {
          console.log('✅ [CustomerDisplay] Fullscreen başarılı (delegasyon ile)');
        } else {
          console.log('⚠️ [CustomerDisplay] Fullscreen başarısız, kullanıcı etkileşimi gerekebilir');
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [requestFullscreen]);

  // Sayfa yüklendiğinde otomatik fullscreen dene
  useEffect(() => {
    // Küçük bir gecikme ile fullscreen dene
    const timer = setTimeout(async () => {
      const success = await requestFullscreen();
      if (!success) {
        console.log('📺 [CustomerDisplay] Otomatik fullscreen başarısız, kullanıcı tıklaması bekleniyor');
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [requestFullscreen]);

  // Tıklama ile fullscreen
  useEffect(() => {
    const handleClick = () => {
      if (!isFullscreen) {
        requestFullscreen();
      }
    };

    document.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [isFullscreen, requestFullscreen]);

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER - Loading
  // ─────────────────────────────────────────────────────────────────────────────
  
  if (loading) {
    return (
      <Box sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: '#000',
        color: '#fff',
      }}>
        <CircularProgress color="inherit" size={60} />
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER - Error
  // ─────────────────────────────────────────────────────────────────────────────
  
  if (error || !settings) {
    return (
      <Box sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: '#000',
        color: '#fff',
        textAlign: 'center',
        p: 4,
      }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Müşteri Ekranı
          </Typography>
          <Typography variant="body1" color="error">
            {error || 'Ayarlar yüklenemedi'}
          </Typography>
          <Typography variant="caption" sx={{ mt: 2, display: 'block' }}>
            Tam ekran için ekrana tıklayın
          </Typography>
        </Box>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER - Disabled
  // ─────────────────────────────────────────────────────────────────────────────
  
  if (!settings.IsEnabled) {
    return (
      <Box sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: '#000',
        color: '#fff',
        textAlign: 'center',
      }}>
        <Typography variant="h5">
          Müşteri Ekranı Pasif
        </Typography>
      </Box>
    );
  }

  const images = settings.Images || [];

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER - Cart View
  // ─────────────────────────────────────────────────────────────────────────────
  
  const renderCartView = () => (
    <Box sx={{
      width: '100%',
      height: '100%',
      bgcolor: settings.CartBackgroundColor || '#ffffff',
      color: settings.CartTextColor || '#000000',
      display: 'flex',
      flexDirection: 'row', // ✅ Yatay düzen - sepet + yan panel
    }}>
      {/* Sol Bölüm: Sepet İçeriği */}
      <Box sx={{
        flex: settings.CartSideImageUrl ? 2 : 1, // Yan panel varsa 2/3, yoksa tam
        display: 'flex',
        flexDirection: 'column',
        p: 4,
      }}>
        <Typography variant="h4" align="center" sx={{ mb: 4 }}>
          Sepetiniz
        </Typography>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {cart.length === 0 ? (
            <Typography variant="h5" align="center" sx={{ mt: 10 }}>
              Sepetiniz boş
            </Typography>
          ) : (
            <Table size="medium">
              <TableBody>
                {cart.map((item, index) => (
                  <TableRow key={`${item.id}-${index}`}>
                    <TableCell sx={{ 
                      fontSize: 24, 
                      color: settings.CartTextColor,
                      borderColor: `${settings.CartTextColor}20`,
                    }}>
                      {item.name}
                    </TableCell>
                    <TableCell align="center" sx={{ 
                      fontSize: 24, 
                      color: settings.CartTextColor,
                      borderColor: `${settings.CartTextColor}20`,
                      width: 100,
                    }}>
                      x{item.quantity}
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontSize: 24, 
                      color: settings.CartTextColor,
                      borderColor: `${settings.CartTextColor}20`,
                      width: 150,
                    }}>
                      ₺{(item.price * item.quantity).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>

        <Box sx={{ 
          mt: 4, 
          pt: 4, 
          borderTop: `2px solid ${settings.CartTextColor}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography sx={{ fontSize: settings.CartTotalFontSize || 48 }}>
            TOPLAM
          </Typography>
          <Typography sx={{ 
            fontSize: settings.CartTotalFontSize || 48, 
            fontWeight: 'bold',
          }}>
            ₺{total.toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* ✅ YENİ: Sağ Bölüm - Yan Panel (Resim + Logo) */}
      {settings.CartSideImageUrl && (
        <Box sx={{
          flex: 1, // 1/3 genişlik
          display: 'flex',
          flexDirection: 'column',
          borderLeft: `1px solid ${settings.CartTextColor}20`,
          bgcolor: settings.CartBackgroundColor || '#ffffff',
        }}>
          {/* Üst Kısım: Seçilen Resim - Büyük alan */}
          <Box sx={{
            flex: 1, // Tüm alanı kapla
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <Box
              component="img"
              src={settings.CartSideImageUrl}
              alt="Kampanya"
              sx={{
                width: '95%',
                height: '95%',
                objectFit: 'contain', // Oranı koruyarak sığdır
              }}
            />
          </Box>

          {/* Alt Kısım: Logo + Powered by - Küçük alan (1/5) */}
          <Box sx={{
            height: '12%', // Yaklaşık 1/5 oran
            minHeight: 60,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            borderTop: `1px solid ${settings.CartTextColor}10`,
            bgcolor: settings.CartBackgroundColor || '#ffffff',
          }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: 10, 
                color: settings.CartTextColor || '#000000',
                opacity: 0.6,
                mb: 0.5,
              }}
            >
              Powered by
            </Typography>
            <Box
              component="img"
              src={BRAND.logos.dark}
              alt={`${BRAND.name} Logo`}
              sx={{
                maxWidth: '70%',
                maxHeight: 30,
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER - Slider View (Carousel)
  // ─────────────────────────────────────────────────────────────────────────────
  
  const renderSliderView = () => {
    if (images.length === 0) {
      return (
        <Box sx={{
          width: '100%',
          height: '100%',
          bgcolor: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#fff',
        }}>
          <Typography variant="h5">
            Reklam resmi eklenmemiş
          </Typography>
        </Box>
      );
    }

    // Tek resim varsa animasyon yok
    if (images.length === 1) {
      return (
        <Box sx={{
          width: '100%',
          height: '100%',
          bgcolor: '#000',
          overflow: 'hidden',
        }}>
          <img
            src={images[0].ImageUrl}
            alt={images[0].ImageTitle || 'Reklam'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
      );
    }

    // Sonraki resim index'i
    const nextIndex = (currentImageIndex + 1) % images.length;

    // Animasyon tipine göre render
    switch (settings.AnimationType) {
      case 'slide':
        // Carousel tarzı slide - iki resim yan yana kayar
        return (
          <Box sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#000',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <Box
              sx={{
                display: 'flex',
                width: '200%',
                height: '100%',
                transform: isTransitioning ? 'translateX(-50%)' : 'translateX(0)',
                transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
              }}
            >
              {/* Mevcut resim */}
              <Box sx={{ width: '50%', height: '100%', flexShrink: 0 }}>
                <img
                  src={images[currentImageIndex].ImageUrl}
                  alt={images[currentImageIndex].ImageTitle || 'Reklam'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              {/* Sonraki resim */}
              <Box sx={{ width: '50%', height: '100%', flexShrink: 0 }}>
                <img
                  src={images[nextIndex].ImageUrl}
                  alt={images[nextIndex].ImageTitle || 'Reklam'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Box>
          </Box>
        );

      case 'fade':
        return (
          <Box sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#000',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <img
              key={currentImageIndex}
              src={images[currentImageIndex].ImageUrl}
              alt={images[currentImageIndex].ImageTitle || 'Reklam'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                animation: `${fadeIn} 1s ease-in-out`,
              }}
            />
          </Box>
        );

      case 'zoom':
        return (
          <Box sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#000',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <img
              key={currentImageIndex}
              src={images[currentImageIndex].ImageUrl}
              alt={images[currentImageIndex].ImageTitle || 'Reklam'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                animation: `${zoomIn} 0.5s ease-out`,
              }}
            />
          </Box>
        );

      case 'flip':
        return (
          <Box sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#000',
            overflow: 'hidden',
            position: 'relative',
            perspective: '1000px',
          }}>
            <img
              key={currentImageIndex}
              src={images[currentImageIndex].ImageUrl}
              alt={images[currentImageIndex].ImageTitle || 'Reklam'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                animation: `${flipIn} 0.6s ease-out`,
              }}
            />
          </Box>
        );

      default: // 'none' veya bilinmeyen
        return (
          <Box sx={{
            width: '100%',
            height: '100%',
            bgcolor: '#000',
            overflow: 'hidden',
          }}>
            <img
              src={images[currentImageIndex].ImageUrl}
              alt={images[currentImageIndex].ImageTitle || 'Reklam'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
        );
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER - Main
  // ─────────────────────────────────────────────────────────────────────────────
  
  return (
    <Box sx={{
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      bgcolor: '#000',
      cursor: 'none',
    }}>
      {showCart ? renderCartView() : renderSliderView()}
    </Box>
  );
};

export default CustomerDisplayScreen;
